import { getSpeakerColor as getSpeakerColorFromDatoId } from '@src/services/SpeakerManagementService';
import {
  fetchSpeakerApiData,
  injectedOverrides,
  SPEAKER_CACHE_INVALIDATED_EVENT,
  speakerCache,
  type SpeakerInfo,
} from '@src/stores-v2/speakerCache';
import { useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import {
  buildSpeakersFromMappings,
  extractSpeakerLabelsFromTranscriptElements,
  getSpeakerColorFromLabel,
  getSpeakerNameFromLabel,
} from '@src/utility/speakerUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';

const logger = LogManager.getLogger('hook:useSpeakerLabels');

/**
 * Hook for loading and resolving speaker information in Creator Studio
 *
 * Loads speaker mappings from the API and Dato speakers to provide:
 * - Speaker name resolution (mapped speakers show real names, unmapped show "Speaker 1", etc.)
 * - Consistent speaker colors
 *
 * Speaker data is cached at the module level so switching views (Redline/Regular)
 * shows speaker labels instantly without re-fetching.
 *
 * @returns Object with getSpeakerName, getSpeakerColor functions and isLoading state
 */
export function useSpeakerLabels() {
  const videoCreator = useVideoCreatorStore();
  const transcriptionStore = useTranscription();
  const storyId = videoCreator.story?.id;

  // Initialize from cache if available
  const [speakerMap, setSpeakerMap] = useState<Map<string, SpeakerInfo>>(
    () => (storyId ? speakerCache.get(storyId) : undefined) ?? new Map(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Listen for cache invalidation events to trigger refresh
  useEffect(() => {
    const handleCacheInvalidated = (
      event: CustomEvent<{ storyId?: string }>,
    ) => {
      // Refresh if no specific storyId or if it matches our storyId
      if (!event.detail.storyId || event.detail.storyId === storyId) {
        setRefreshTrigger(prev => prev + 1);
      }
    };

    window.addEventListener(
      SPEAKER_CACHE_INVALIDATED_EVENT,
      handleCacheInvalidated as EventListener,
    );
    return () => {
      window.removeEventListener(
        SPEAKER_CACHE_INVALIDATED_EVENT,
        handleCacheInvalidated as EventListener,
      );
    };
  }, [storyId]);

  // Compute a fingerprint of the current speaker label set.
  // This hook runs inside observer() components, so reading
  // transcriptionStore.transcriptionElements triggers MobX tracking.
  // When the label set changes (e.g., video-level override loaded),
  // the fingerprint changes → the load effect re-runs → map rebuilds
  // using cached API data (no network round-trip).
  const labelFingerprint = useMemo(() => {
    const elements = transcriptionStore.getFinalTranscriptionElements();
    const labels = extractSpeakerLabelsFromTranscriptElements(elements);
    return labels.sort().join('\0');
  }, [transcriptionStore.getFinalTranscriptionElements()]);

  // NOTE: Window focus-based cache clearing was removed because it caused
  // a 10+ second DatoCMS refetch every time the user clicked the tab.
  // The cache is invalidated via clearSpeakerCache() after actual mutations
  // (assign, unassign, create speaker), which is sufficient.

  // Load speaker data when story changes, cache is cleared, labels change, or refresh is triggered
  useEffect(() => {
    if (!storyId) return;

    // If cached data exists and the label fingerprint hasn't changed,
    // reuse it (e.g., view switches between Redline/Regular).
    const cachedData = speakerCache.get(storyId);
    if (cachedData && cachedData.size > 0) {
      // Check if cached map already covers the current label set
      const currentLabels = labelFingerprint.split('\0').filter(Boolean);
      const allCovered = currentLabels.every(l => cachedData.has(l));
      if (allCovered) {
        setSpeakerMap(cachedData);
        return;
      }
    }

    let cancelled = false;

    const loadSpeakerData = async () => {
      setIsLoading(true);
      try {
        const transcriptElements =
          transcriptionStore.getFinalTranscriptionElements();
        // Always merge both sources — final elements may lack override_* labels
        // that only exist in originalTranscription.elements
        const originalElements =
          transcriptionStore.getOriginalTranscription()?.elements || [];
        const labelSet = new Set(
          extractSpeakerLabelsFromTranscriptElements(transcriptElements),
        );
        for (const label of extractSpeakerLabelsFromTranscriptElements(
          originalElements,
        )) {
          labelSet.add(label);
        }
        const speakerLabels = Array.from(labelSet);

        if (speakerLabels.length === 0) {
          if (!cancelled) {
            const emptyMap = new Map<string, SpeakerInfo>();
            speakerCache.set(storyId, emptyMap);
            setSpeakerMap(emptyMap);
            setIsLoading(false);
          }
          return;
        }

        const [speakerMappings, datoSpeakers] = await fetchSpeakerApiData(
          storyId,
          videoCreator.datoClientStore,
        );

        if (cancelled) return;

        // Build speakers from mappings - this handles the Dato speaker lookup
        const speakers = buildSpeakersFromMappings(
          speakerLabels,
          speakerMappings,
          datoSpeakers,
        );

        // Create a lookup map for speaker labels to speaker info
        const speakersLookup = new Map(speakers.map(s => [s.speakerLabel, s]));

        // Build speaker map with name and color for all labels
        // Mapped speakers use Dato-speaker-ID-based color (consistent with Speakers panel dot)
        // Unmapped speakers use voice-label-based color
        // Build a Dato speaker lookup by ID for override resolution
        const datoSpeakerById = new Map(datoSpeakers.map(s => [s.id, s]));
        // Reverse lookup: datoSpeakerId → speakerLabel (O(1) instead of O(M) per override)
        const datoIdToLabel = new Map(
          Object.entries(speakerMappings?.mappings || {}).map(([label, id]) => [
            id,
            label,
          ]),
        );

        const map = new Map<string, SpeakerInfo>();
        speakerLabels.forEach(label => {
          // For override_* labels, resolve the Dato speaker by ID embedded in the label
          if (label.startsWith('override_')) {
            const datoId = label.slice('override_'.length);

            // Strategy 1: find existing spk_X mapped to same datoId, reuse its name
            const existingLabel = datoIdToLabel.get(datoId);
            const existingSpeaker = existingLabel
              ? speakersLookup.get(existingLabel)
              : undefined;
            if (existingSpeaker) {
              map.set(label, {
                name: existingSpeaker.name || 'Unknown',
                color: getSpeakerColorFromDatoId(datoId, 0),
                title: existingSpeaker.title || undefined,
              });
              return;
            }

            // Strategy 2: look up in fetched Dato speakers (story + organization)
            const datoSpeaker = datoSpeakerById.get(datoId);
            if (datoSpeaker) {
              map.set(label, {
                name: datoSpeaker.name || 'Unknown',
                color: getSpeakerColorFromDatoId(datoId, 0),
                title: datoSpeaker.title || undefined,
              });
              return;
            }
          }

          // Standard label resolution via voice mappings
          const speaker = speakersLookup.get(label);
          const datoSpeakerId = speakerMappings?.mappings?.[label];
          map.set(label, {
            name: speaker?.name || getSpeakerNameFromLabel(label),
            color: datoSpeakerId
              ? getSpeakerColorFromDatoId(datoSpeakerId, 0)
              : getSpeakerColorFromLabel(label),
            title: speaker?.title || undefined,
          });
        });

        // Merge any injected overrides (session-only, not persisted)
        const overrides = injectedOverrides.get(storyId);
        if (overrides) {
          for (const [label, info] of overrides) {
            map.set(label, info);
          }
        }

        // Cache for future use
        speakerCache.set(storyId, map);
        setSpeakerMap(map);
      } catch (error) {
        logger.error('Error loading speaker data:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadSpeakerData();

    return () => {
      cancelled = true;
    };
  }, [
    storyId,
    transcriptionStore,
    videoCreator.datoClientStore,
    refreshTrigger,
    labelFingerprint,
  ]);

  // Always return functions that work - they use fallback values if cache is empty
  const getSpeakerName = useCallback(
    (speakerLabel: string | undefined): string => {
      if (!speakerLabel) return 'Unknown';
      return (
        speakerMap.get(speakerLabel)?.name ||
        getSpeakerNameFromLabel(speakerLabel)
      );
    },
    [speakerMap],
  );

  const getSpeakerColor = useCallback(
    (speakerLabel: string | undefined): string => {
      if (!speakerLabel) return '#A9A9A9';
      return (
        speakerMap.get(speakerLabel)?.color ||
        getSpeakerColorFromLabel(speakerLabel)
      );
    },
    [speakerMap],
  );

  const getSpeakerTitle = useCallback(
    (speakerLabel: string | undefined): string | undefined => {
      if (!speakerLabel) return undefined;
      return speakerMap.get(speakerLabel)?.title;
    },
    [speakerMap],
  );

  return useMemo(
    () => ({
      getSpeakerName,
      getSpeakerColor,
      getSpeakerTitle,
      isLoading,
    }),
    [getSpeakerName, getSpeakerColor, getSpeakerTitle, isLoading],
  );
}
